import React, { Fragment, useMemo } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import SelectionIndicator from 'components/selection-indicator';
import { Size } from 'components/selection-indicator/constants';
import { useLocale } from 'state/intl';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import { formatCurrencyCodeForRegion } from 'utils/payment/format-currency-code-for-region';

import {
  Disclaimer,
  LanguageSelectionCell,
  LanguageSelectionContainer,
  ModalAdditionalContentWrapper,
} from './styled';

const LanguageSelectionLanguageText = styled.span`
  font-weight: ${Styles.fontWeight.heavy};
  padding-bottom: 2px;
`;

const LanguageSelectionLabel = styled(LanguageSelectionCell)<{ padding?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: ${p => p.padding || '12px 24px'};
`;

const LanguageIndicatorCell = styled(LanguageSelectionCell)`
  position: relative;
`;

interface ILanguageModalContentsProps {
  regionFilter: (region: string) => boolean;
  disclaimer?: string;
  applyButtonText: string;
  setLanguage: () => void;
  handleSelection: (
    selectedLanguage: string,
    mappedRegion: string,
    isOneStep: boolean
  ) => () => void;
  regionKey: string;
  langKey: string;
  /**
   * Pass the id of the heading that describes the radio group.
   */
  ariaRadioGroupLabeledBy: string;
}

const LanguageSelectorModalContents: React.FC<ILanguageModalContentsProps> = ({
  regionFilter,
  regionKey,
  langKey,
  disclaimer,
  applyButtonText,
  setLanguage,
  handleSelection,
  ariaRadioGroupLabeledBy = '',
}) => {
  const { formatMessage } = useIntl();
  const { readableRegions, readableLanguages, supportedLocales, region } = useLocale();
  const singleRegionLanguageSelector = useFlag(LaunchDarklyFlag.SINGLE_REGION_LANGUAGE_SELECTOR);

  const locales = useMemo(
    () =>
      singleRegionLanguageSelector
        ? supportedLocales.filter(l => l.region === region)
        : supportedLocales,
    [region, singleRegionLanguageSelector, supportedLocales]
  );

  const regionLanguageSelections = locales
    .filter(({ region: filteredRegion }) => regionFilter(filteredRegion))
    .map(({ region: mappedRegion, language: mappedLanguage }) => (
      <Fragment key={`dialog-button-${mappedLanguage}-${mappedRegion}`}>
        <LanguageSelectionCell />
        <LanguageIndicatorCell
          data-testid={`dialog-button-${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`}
          onClick={handleSelection(mappedLanguage, mappedRegion, false)}
        >
          <SelectionIndicator
            lang={`${mappedLanguage.toLowerCase()}-${mappedRegion}`}
            option={{
              name: {
                _type: 'localeString',
                locale: `${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`,
              },
            }}
            checked={regionKey === mappedRegion && langKey === mappedLanguage}
            type="radio"
            aria-label={`${formatMessage({ id: 'select' })} ${
              readableLanguages[mappedLanguage]
            } / ${mappedRegion}`}
            size={Size.REGULAR}
            uiPattern="radio"
            hasBackground
          />
        </LanguageIndicatorCell>
        <LanguageSelectionLabel padding={singleRegionLanguageSelector ? '22px 24px' : undefined}>
          <LanguageSelectionLanguageText lang={mappedLanguage.toLowerCase()}>
            {readableLanguages[mappedLanguage]}
          </LanguageSelectionLanguageText>
          {singleRegionLanguageSelector || (
            <span
              data-testid={`dialog-code-${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`}
            >
              {formatCurrencyCodeForRegion(readableRegions[mappedRegion])}
            </span>
          )}
        </LanguageSelectionLabel>
        <LanguageSelectionCell />
      </Fragment>
    ));

  return (
    <>
      <LanguageSelectionContainer role="radiogroup" aria-labelledby={ariaRadioGroupLabeledBy}>
        {regionLanguageSelections}
      </LanguageSelectionContainer>
      {disclaimer && (
        <ModalAdditionalContentWrapper>
          <Disclaimer>{disclaimer}</Disclaimer>
        </ModalAdditionalContentWrapper>
      )}
      <Box center width="100%" margin="0 0 2rem">
        <ActionButton onClick={setLanguage} data-testid="action-button-apply-language-selection">
          {applyButtonText}
        </ActionButton>
      </Box>
    </>
  );
};

export default LanguageSelectorModalContents;
